/**
 * Chave de agrupamento das falhas equivalentes.
 *
 * Lógica pura: sem browser, sem SDK e sem travessia para o grafo Angular. A entrada é a
 * mensagem já sanitizada, e o stack do erro não é lido em nenhum ponto — é o que mantém o
 * agrupamento estável entre builds, em que o mesmo defeito produz frames diferentes.
 */
import { ErrorOrigin, ErrorScope } from './types';

export const NUMBER_PLACEHOLDER = '<n>';
export const UUID_PLACEHOLDER = '<uuid>';
export const HEX_PLACEHOLDER = '<hex>';

/** Nome usado quando o erro observado não expõe um `name` aproveitável. */
const FALLBACK_ERROR_NAME = 'Error';

/** O escopo participa sempre do cálculo; sem declaração, entra como este marcador. */
const ABSENT_SCOPE_MARKER = '-';

const UUID_PATTERN = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
const HEX_PATTERN = /\b[0-9a-f]{8,}\b/gi;
const DIGITS_PATTERN = /\d{4,}/g;

const FNV_32_OFFSET_BASIS = 0x811c9dc5;
const FNV_32_PRIME = 0x01000193;
const HASH_HEX_LENGTH = 8;

export interface FingerprintInput {
  readonly origin: ErrorOrigin;
  readonly scope?: ErrorScope;
  readonly name: string;
  /** Mensagem já sanitizada. */
  readonly message: string;
}

/** Colapsa variação de identificador para que falhas equivalentes agrupem juntas. */
export function normalizeMessageForFingerprint(message: string): string {
  if (typeof message !== 'string') {
    return '';
  }

  // UUID antes de hex: a regra de hex consumiria os segmentos do UUID em pedaços e
  // produziria duas grafias do mesmo identificador, logo dois grupos onde deve haver um.
  return message
    .replace(UUID_PATTERN, UUID_PLACEHOLDER)
    .replace(HEX_PATTERN, HEX_PLACEHOLDER)
    .replace(DIGITS_PATTERN, NUMBER_PLACEHOLDER);
}

/**
 * Compõe a chave de agrupamento a partir de origem, escopo, nome e mensagem normalizada.
 *
 * Os três primeiros campos ficam em claro para o grupo ser legível direto no painel; a
 * mensagem entra hasheada para que até mil caracteres de texto não virem chave de
 * agrupamento.
 */
export function buildFingerprint(input: FingerprintInput): string {
  const scope = usableText(input.scope) ?? ABSENT_SCOPE_MARKER;
  const name = usableText(input.name) ?? FALLBACK_ERROR_NAME;
  const normalizedMessage = normalizeMessageForFingerprint(input.message);

  return `${input.origin}:${scope}:${name}:${hash32(normalizedMessage)}`;
}

function usableText(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/** FNV-1a de 32 bits em hexadecimal de largura fixa, sem dependência externa. */
function hash32(value: string): string {
  let hash = FNV_32_OFFSET_BASIS;

  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, FNV_32_PRIME);
  }

  return (hash >>> 0).toString(16).padStart(HASH_HEX_LENGTH, '0');
}
